import { ProvideAuth } from 'module/auth/context/AuthContext';
import React from 'react';
import { createRoot } from 'react-dom/client'; // ✅ React 18 method
import App from './App';
import reportWebVitals from './reportWebVitals';
import { clearOldCacheEntriesByCount } from 'core/utils/localStorageUtils';

// react query setup
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

// import styles
import { ToastContextProvider } from 'core/components/Toast/context/ToastContext';
import { WorkplacesContextProvider } from 'module/workplaces/context/WorkplacesContext';
import 'rsuite/dist/rsuite.min.css';
import './index.scss';
import './styles/chat.scss';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { ThemeProvider } from '@mui/material';
import { theme } from 'styles/mui/theme';

// Suppress ResizeObserver loop errors (harmless browser quirk)
const resizeObserverLoopErrRe = /^[^(]*ResizeObserver[^)]*$/;
const resizeObserverLoopErrRe2 = /ResizeObserver loop completed with undelivered notifications/;

// Suppress console.error for ResizeObserver
const originalError = window.console.error;
window.console.error = (...args: any[]) => {
	if (
		args.length > 0 &&
		typeof args[0] === 'string' &&
		(resizeObserverLoopErrRe.test(args[0]) || resizeObserverLoopErrRe2.test(args[0]))
	) {
		return;
	}
	originalError.apply(window.console, args);
};

// Suppress global error handler for ResizeObserver
const originalErrorHandler = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
	if (
		typeof message === 'string' &&
		(resizeObserverLoopErrRe.test(message) || resizeObserverLoopErrRe2.test(message))
	) {
		return true; // Suppress the error
	}
	if (originalErrorHandler) {
		return originalErrorHandler(message, source, lineno, colno, error);
	}
	return false;
};

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			suspense: false,
		},
	},
});

// if (process.env.REACT_APP_SENTRY_ENV === 'staging') {
// 	Sentry.init({
// 		dsn: 'https://598ff86a1059408b8b90596b377717b2@sentry.q-tests.com/49',
// 		integrations: [new Integrations.BrowserTracing()],
// 		tracesSampleRate: 1.0,
// 	});
// }

// ✅ React 18 root rendering
const container = document.getElementById('root');
if (container) {
	// Clear old cache entries on app startup to prevent quota issues
	try {
		const cachePrefixes = [
			'tv_package_channels_cache',
			'preference_channels_cache',
			'gracenote_lineups_cache',
		];
		
		cachePrefixes.forEach(prefix => {
			clearOldCacheEntriesByCount(prefix, 3); // Keep only 3 most recent entries per prefix
		});
	} catch (e) {
		console.warn('Failed to clear old cache on startup:', e);
	}

	const root = createRoot(container);
	root.render(
		<React.StrictMode>
			<ThemeProvider theme={theme}>
				<ToastContextProvider>
					<ProvideAuth>
						<QueryClientProvider client={queryClient}>
							<WorkplacesContextProvider>
								<App />
								<ReactQueryDevtools initialIsOpen={false} />
							</WorkplacesContextProvider>
						</QueryClientProvider>
					</ProvideAuth>
				</ToastContextProvider>
			</ThemeProvider>
		</React.StrictMode>
	);
}

reportWebVitals();